<template>
  <table class="table table-sm mb-0 receipt-info-table">
    <tr>
      <td class="font-weight-bold border-top-0">{{ $t('Receipt') }}</td>
      <td class="border-top-0">{{ receipt.receipt_no }}</td>
    </tr>
    <tr>
      <td class="font-weight-bold">{{ $t('Receipt Type') }}</td>
      <td>{{ receipt.receipt_type | removeUnderscores | toUpperCaseEveryWord }}</td>
    </tr>
    <tr>
      <td class="font-weight-bold">{{ $t('Branch') }}</td>
      <td>
        <router-link v-if="receipt.branch" :to="{name: 'branches', params: {branch_uuid: receipt.branch_uuid}}">
          {{ receipt.branch.name }}
        </router-link>
      </td>
    </tr>
    <tr>
      <td class="font-weight-bold">{{ $t('Department') }}</td>
      <td>
        <template v-if="receipt.department">
          <router-link :to="{name: 'departments', params: {department_uuid: receipt.department_uuid}}">
            {{ receipt.department.name | masterLanguage }}
          </router-link>
        </template>
      </td>
    </tr>
    <tr>
      <td class="font-weight-bold">{{ $t('Multi Item-Location') }}</td>
      <td>
        <span class="badge bg-success text-white" v-if="receipt.enable_multiple_locations">{{ $t('Yes') }}</span>
        <span class="badge bg-danger text-white" v-else>{{ $t('No') }}</span>
      </td>
    </tr>
    <template v-if="receipt.item_locations && receipt.item_locations.length">
      <template v-for="(itemLocation, index) in receipt.item_locations">
        <tr>
          <td class="font-weight-bold">Location {{index + 1}} <span class="badge bg-success text-white" v-if="itemLocation.is_primary">{{ $t('Primary') }}</span></td>
          <td >{{itemLocation.item_location.name}} </td>
        </tr>
        <tr v-if="itemLocation.container_type">
          <td class="font-weight-bold">{{$t('Container Type')}} {{index + 1}}</td>
          <td >{{itemLocation.container_type}} </td>
        </tr>
        <tr v-if="itemLocation.container_count">
          <td class="font-weight-bold">{{$t('Container Count')}} {{index + 1}}</td>
          <td >{{itemLocation.container_count}} </td>
        </tr>
        <tr v-if="itemLocation.note">
          <td class="font-weight-bold">{{$t('Note')}} {{index + 1}}</td>
          <td >{{itemLocation.note}} </td>
        </tr>
      </template>
    </template>
    <tr>
      <td class="font-weight-bold">{{ $t('Recommended By Customer') }}</td>
      <td>
        <router-link :to="{name: 'customers.view', params: {id: receipt.recommended_by_uuid}}">
          {{ receipt.recommended_by && receipt.recommended_by.display_name }}
        </router-link>
      </td>
    </tr>
    <tr>
      <td class="font-weight-bold">{{ $t('Recommended By User') }}</td>
      <td>
        <span v-if="receipt.recommended_by_user">
        {{ receipt.recommended_by_user.display_name }} - {{ receipt.recommended_by_user.email }}
        </span>
      </td>
    </tr>
    <tr>
      <td class="font-weight-bold">{{ $t('Specialists') }}</td>
      <td>
        <ul v-if="receipt.specialists" style="list-style: none; padding-left: 0;">
          <li v-for="specialist in receipt.specialists" :key="specialist.uuid">
            <span >
            {{ specialist.display_name }} - {{ specialist.email }}
            </span>
          </li>
        </ul>
      </td>
    </tr>
    <tr>
      <td class="font-weight-bold">{{ $t('Date') }}</td>
      <td>{{ receipt.date | toDate }}</td>
    </tr>
    <tr>
      <td class="font-weight-bold">{{ $t('Description') }}</td>
      <td>{{ receipt.description }}</td>
    </tr>
    <tr v-if="enableReceiptStatus">
      <td class="font-weight-bold">{{ $t('Status') }}</td>
      <td>
        <span v-as-status="receipt.status">{{ getStatusLabel(receipt.status) }}</span>
      </td>
    </tr>
    <tr>
      <td class="font-weight-bold">{{ $t('Consignment Status') }}</td>
      <td>
        <span v-if="receipt.receipt_contract_status" v-as-status="receipt.receipt_contract_status">
          {{ receipt.receipt_contract_status | removeUnderscores | toUpperCaseEveryWord }}
        </span>
        <span v-as-status="receipt.receipt_contract_status" v-else>{{ $t('N/A') }}</span>
      </td>
    </tr>
    <tr>
      <td class="font-weight-bold">{{ $t('Pre-Consignment Status') }}</td>
      <td>
        <span v-if="receipt.pre_receipt_contract_status" v-as-status="receipt.pre_receipt_contract_status">
          {{ receipt.pre_receipt_contract_status | removeUnderscores | toUpperCaseEveryWord }}
        </span>
        <span v-as-status="receipt.pre_receipt_contract_status" v-else>{{ $t('N/A') }}</span>
      </td>
    </tr>
    <tr>
      <td class="font-weight-bold">{{ $t('Number of Containers') }}</td>
      <td>
        <span>{{ receipt.containers_count }}</span>
      </td>
    </tr>
    <tr>
      <td class="font-weight-bold">{{ $t('Type of Container') }}</td>
      <td>
        <span>{{ receipt.container_type }}</span>
      </td>
    </tr>
    <tr>
      <td class="font-weight-bold">{{ $t('Representative User') }}</td>
      <td>
        <span v-if="receipt.rep_user">{{ receipt.rep_user.display_name }}</span>
      </td>
    </tr>
    <tr>
      <td class="font-weight-bold">{{ $t('Dropper') }}</td>
      <td>
        <span v-if="receipt.dropper">{{ receipt.dropper.display_name }}</span>
      </td>
    </tr>
  </table>
</template>

<script>
  import {createNamespacedHelpers} from "vuex";

  const {mapState: globalMapState} = createNamespacedHelpers('global');

  export default {
    name: "ReceiptInfoTable",
    props: ['receipt'],
    computed: {
      ...globalMapState({
        enableReceiptStatus: state => state.enableReceiptStatus,
        receiptStatuses: state => state.receiptStatuses,
      }),
    },
    methods: {
      getStatusLabel(val) {
        if (val && this.receiptStatuses && this.receiptStatuses.length > 0) {
          let obj = this.receiptStatuses.find(s => s.value === val);
          return obj ? obj.label : val
        }
        return val
      }
    }
  }
</script>

<style lang="scss" scoped>
  .receipt-info-table {
    tr {
      td:first-child {
        width: 12em;
      }
    }
  }
</style>
